import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useSearchParams,
} from "react-router-dom";

import axios from "axios";

import { Helmet } from "react-helmet-async";

import {
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  BrainCircuit,
  LayoutDashboard,
  ShoppingCart,
  Smartphone,
  Users,
  CheckCircle2,
  Check,
  AlertCircle,
  Loader2,
  MessageCircle,
  Bot,
  X,
  RotateCcw,
} from "lucide-react";

import "./ContactPage.css";

/* =========================================================
   STATIC FORM OPTIONS
========================================================= */

const PROJECT_TYPES = [
  {
    key: "ecommerce",
    icon: <ShoppingCart size={28} />,
    title: "Ecommerce Platform",
  },
  {
    key: "mobile",
    icon: <Smartphone size={28} />,
    title: "Mobile App",
  },
  {
    key: "crm",
    icon: <Users size={28} />,
    title: "CRM / MIS Software",
  },
  {
    key: "ai",
    icon: <BrainCircuit size={28} />,
    title: "AI Solutions",
  },
  {
    key: "custom-software",
    icon: <LayoutDashboard size={28} />,
    title: "Custom Software",
  },
];


const BUDGET_RANGES = [
  "Below ₹50K",
  "₹50K - ₹1L",
  "₹1L - ₹3L",
  "₹3L - ₹5L",
  "₹5L+",
  "Need Consultation",
];


const TIMELINES = [
  "Within 1 Month",
  "1 - 3 Months",
  "3 - 6 Months",
  "6 Months+",
  "Flexible",
];


const SERVICE_PROJECT_MAP = {
  ecommerce: "Ecommerce Platform",
  mobile: "Mobile App",
  crm: "CRM / MIS Software",
  ai: "AI Solutions",
  dashboard: "Custom Software",
  "custom-software": "Custom Software",
};

/* =========================================================
   CONFIGURATION
========================================================= */

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://backendhub.co.in/bcservice"
).replace(/\/+$/, "");

const LEAD_API_URL =
  `${API_BASE_URL}/api/leads/`;


/* =========================================================
   PROJECT TYPES
========================================================= */

const whyChooseUs = [
  "Business-Focused Software Development",
  "Scalable Cloud Architecture",
  "Modern Web & Mobile Applications",
  "Long-Term Technical Support",
];


/* =========================================================
   INITIAL FORM
========================================================= */

const initialFormState = {
  fullName: "",
  email: "",
  phone: "",
  companyName: "",

  projectType: "",

  budgetRange: "",
  timeline: "",
  projectDetails: "",

  /* Ecommerce */
  ecommerceBusinessModel: "",
  productCount: "",
  existingWebsite: "",
  paymentGatewayRequired: "",
  ecommerceMobileAppRequired: "",

  /* Mobile */
  mobilePlatforms: "",
  mobileAppType: "",
  backendAvailable: "",

  /* CRM */
  crmUsers: "",
  crmType: "",
  currentSystem: "",
  crmMobileAppRequired: "",

  /* AI */
  aiUseCase: "",
  existingDataAvailable: "",

  /* Custom Software */
  softwarePurpose: "",
  currentProcess: "",
};


/* =========================================================
   CUSTOM DROPDOWN
   Avoids Windows/Chrome native <select> popup rendering.
========================================================= */

function CustomSelect({
  id,
  name,
  value,
  placeholder,
  options,
  onChange,
  error = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  const normalizedOptions =
    options.map((option) => {
      if (typeof option === "string") {
        return {
          value: option,
          label: option,
        };
      }

      return option;
    });

  const selectedOption =
    normalizedOptions.find(
      (option) =>
        option.value === value
    );

  const handleSelect = (
    selectedValue
  ) => {
    onChange({
      target: {
        name,
        value: selectedValue,
      },
    });

    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      setIsOpen((previous) => !previous);
    }

    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="custom-select-wrapper"
    >
      <button
        id={id}
        type="button"
        className={`custom-select-trigger ${
          isOpen
            ? "custom-select-trigger-open"
            : ""
        } ${
          error
            ? "custom-select-trigger-error"
            : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() =>
          setIsOpen(
            (previous) => !previous
          )
        }
        onKeyDown={handleKeyDown}
      >
        <span
          className={
            selectedOption
              ? "custom-select-value"
              : "custom-select-placeholder"
          }
        >
          {selectedOption?.label ||
            placeholder}
        </span>

        <span
          className={`custom-select-arrow ${
            isOpen
              ? "custom-select-arrow-open"
              : ""
          }`}
          aria-hidden="true"
        >
          ▼
        </span>
      </button>

      {isOpen && (
        <div
          className="custom-select-menu"
          role="listbox"
          aria-labelledby={id}
        >
          {normalizedOptions.map(
            (option) => {
              const selected =
                option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={
                    selected
                  }
                  className={`custom-select-option ${
                    selected
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    handleSelect(
                      option.value
                    )
                  }
                >
                  <span>
                    {option.label}
                  </span>

                  {selected && (
                    <Check
                      size={16}
                    />
                  )}
                </button>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}


/* =========================================================
   COMPONENT
========================================================= */

export default function ContactPage() {

  const [searchParams] = useSearchParams();

  const formSectionRef = useRef(null);


  /* =======================================================
     FORM STATE
  ======================================================= */

  const [formData, setFormData] =
    useState(initialFormState);


  const [errors, setErrors] =
    useState({});


  const [submitting, setSubmitting] =
    useState(false);


  const [submitStatus, setSubmitStatus] =
    useState({
      type: "",
      message: "",
    });

  const [submitProgress, setSubmitProgress] =
    useState(0);

  const [submitStage, setSubmitStage] =
    useState("");

  const [createdLeadId, setCreatedLeadId] =
    useState(null);

  const [botOpen, setBotOpen] =
    useState(false);

  const [botMessage, setBotMessage] =
    useState(
      "Hello! I can help with project scope, budget, timeline, or the next steps."
    );


  /* =======================================================
     ACQUISITION CONTEXT
  ======================================================= */

  const service =
    searchParams.get("service") || "";


  const acquisitionData = useMemo(
    () => ({
      sourceChannel:
        searchParams.get("utm_source") === "google"
          ? "GOOGLE"
          : "WEBSITE",

      sourceType: "INBOUND",

      service,

      utmSource:
        searchParams.get("utm_source") || "",

      utmMedium:
        searchParams.get("utm_medium") || "",

      utmCampaign:
        searchParams.get("utm_campaign") || "",

      utmTerm:
        searchParams.get("utm_term") || "",

      utmContent:
        searchParams.get("utm_content") || "",

      referrer:
        document.referrer || "",

      landingPage:
        window.location.pathname,

      acquiredAt:
        new Date().toISOString(),
    }),
    [searchParams, service]
  );


  /* =======================================================
     AUTO SELECT PROJECT FROM QUERY PARAMETER
  ======================================================= */

  useEffect(() => {

    const project =
      SERVICE_PROJECT_MAP[service];

    if (!project) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      projectType: project,
    }));

  }, [service]);


  /* =======================================================
     SCROLL TO FORM
  ======================================================= */

  const scrollToForm = () => {

    formSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  };


  /* =======================================================
     PROJECT SELECT
  ======================================================= */

  const selectProject = (projectTitle) => {

    setFormData((previous) => ({
      ...previous,
      projectType: projectTitle,
    }));

    setErrors((previous) => ({
      ...previous,
      projectType: "",
    }));

    setTimeout(() => {
      scrollToForm();
    }, 100);
  };


  /* =======================================================
     GENERIC CHANGE HANDLER
  ======================================================= */

  const handleChange = (event) => {

    const {
      name,
      value,
    } = event.target;


    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));


    if (errors[name]) {

      setErrors((previous) => ({
        ...previous,
        [name]: "",
      }));

    }

  };


  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm = () => {

    const newErrors = {};


    if (!formData.fullName.trim()) {
      newErrors.fullName =
        "Please enter your name.";
    }


    if (!formData.email.trim()) {

      newErrors.email =
        "Please enter your email.";

    } else {

      const emailRegex =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(formData.email)) {
        newErrors.email =
          "Please enter a valid email address.";
      }

    }


    if (!formData.phone.trim()) {
      newErrors.phone =
        "Please enter your phone number.";
    }


    if (!formData.projectType) {
      newErrors.projectType =
        "Please select a project type.";
    }


    if (!formData.projectDetails.trim()) {
      newErrors.projectDetails =
        "Please tell us briefly about your requirement.";
    }


    setErrors(newErrors);


    return (
      Object.keys(newErrors).length === 0
    );

  };


  /* =======================================================
     BUILD QUALIFICATION OBJECT
  ======================================================= */

  const getQualificationData = () => {

    switch (formData.projectType) {

      case "Ecommerce Platform":

        return {
          businessModel:
            formData.ecommerceBusinessModel,

          productCount:
            formData.productCount,

          existingWebsite:
            formData.existingWebsite,

          paymentGatewayRequired:
            formData.paymentGatewayRequired,

          mobileAppRequired:
            formData.ecommerceMobileAppRequired,
        };


      case "Mobile App":

        return {
          platforms:
            formData.mobilePlatforms,

          appType:
            formData.mobileAppType,

          backendAvailable:
            formData.backendAvailable,
        };


      case "CRM / MIS Software":

        return {
          approximateUsers:
            formData.crmUsers,

          crmType:
            formData.crmType,

          currentSystem:
            formData.currentSystem,

          mobileAppRequired:
            formData.crmMobileAppRequired,
        };


      case "AI Solutions":

        return {
          aiUseCase:
            formData.aiUseCase,

          existingDataAvailable:
            formData.existingDataAvailable,
        };


      case "Custom Software":

        return {
          softwarePurpose:
            formData.softwarePurpose,

          currentProcess:
            formData.currentProcess,
        };


      default:
        return {};
    }

  };


  /* =======================================================
     CONTACT HELPERS
  ======================================================= */

  const getReferenceCode = () => {
    if (!createdLeadId) {
      return "";
    }

    return `BC-${String(
      createdLeadId
    ).padStart(5, "0")}`;
  };


  const openWhatsApp = () => {

    const reference =
      getReferenceCode();

    const message =
      `Hello Bairagi Consultants. ` +
      `I have submitted a consultation request` +
      `${reference ? ` (${reference})` : ""}. ` +
      `I would like to discuss my ${formData.projectType || "software"} requirement.`;

    const whatsappUrl =
      `https://wa.me/919432169467?text=${encodeURIComponent(
        message
      )}`;

    window.open(
      whatsappUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };


  const handleBotQuickReply = (
    topic
  ) => {

    const replies = {
      scope:
        "Please share the main business problem, users involved, must-have features and any existing software. Our team will use that information to prepare for the discussion.",

      budget:
        "The final estimate depends on scope, integrations, design, mobile requirements and deployment. Your submitted budget range helps us recommend the right implementation approach.",

      timeline:
        "We normally confirm the delivery timeline after reviewing the scope and dependencies. Your request will be reviewed and we will respond within 24 hours.",

      next:
        "Your request has been saved successfully. Our team will review it and contact you within 24 hours. You can also continue immediately on WhatsApp.",
    };

    setBotMessage(
      replies[topic] ||
      "Please choose one of the available topics."
    );
  };


  const resetConsultationForm = () => {

    const preservedProject =
      formData.projectType;

    setFormData({
      ...initialFormState,
      projectType:
        preservedProject,
    });

    setErrors({});
    setSubmitStatus({
      type: "",
      message: "",
    });
    setSubmitProgress(0);
    setSubmitStage("");
    setCreatedLeadId(null);
    setBotOpen(false);
  };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const delay = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms));


  const handleSubmit = async (event) => {

    event.preventDefault();
    event.stopPropagation();

    console.log(
      "========== CONTACT FORM SUBMIT HANDLER CALLED =========="
    );

    console.log(
      "Current form data:",
      formData
    );

    setSubmitStatus({
      type: "",
      message: "",
    });

    setCreatedLeadId(null);
    setSubmitProgress(0);
    setSubmitStage("");

    /* -------------------------------------------------------
       STEP 1 - VALIDATE
    ------------------------------------------------------- */

    setSubmitProgress(10);
    setSubmitStage(
      "Checking your project information..."
    );

    const isValid =
      validateForm();

    console.log(
      "Form validation result:",
      isValid
    );

    if (!isValid) {

      console.log(
        "Submission stopped because validation failed."
      );

      setSubmitProgress(0);
      setSubmitStage("");

      setSubmitStatus({
        type: "error",
        message:
          "Please review the highlighted required fields before submitting your request.",
      });

      return;
    }


    /* -------------------------------------------------------
       STEP 2 - PREPARE PAYLOAD
    ------------------------------------------------------- */

    setSubmitting(true);

    setSubmitProgress(20);
    setSubmitStage(
      "Preparing your consultation request..."
    );

    await delay(250);

    const payload = {

      fullName:
        formData.fullName.trim(),

      email:
        formData.email.trim(),

      phone:
        formData.phone.trim(),

      companyName:
        formData.companyName.trim() || null,

      projectType:
        formData.projectType,

      budgetRange:
        formData.budgetRange || null,

      timeline:
        formData.timeline || null,

      projectDetails:
        formData.projectDetails.trim(),

      qualification:
        getQualificationData(),

      acquisition:
        acquisitionData,

      leadStatus:
        "NEW",
    };


    try {

      /* -----------------------------------------------------
         STEP 3 - CONNECT
      ----------------------------------------------------- */

      setSubmitProgress(40);
      setSubmitStage(
        "Connecting to our consultation service..."
      );

      await delay(250);

      console.log(
        "Calling API:",
        LEAD_API_URL
      );

      console.log(
        "Lead Request Payload:",
        payload
      );


      /* -----------------------------------------------------
         STEP 4 - SEND REQUEST
      ----------------------------------------------------- */

      setSubmitProgress(65);
      setSubmitStage(
        "Submitting your project requirement..."
      );

      const response = await axios.post(
        LEAD_API_URL,
        payload,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          timeout: 20000,
        }
      );


      /* -----------------------------------------------------
         STEP 5 - RESPONSE RECEIVED
      ----------------------------------------------------- */

      setSubmitProgress(90);
      setSubmitStage(
        "Request received. Finalising..."
      );

      console.log(
        "Lead API Response:",
        response.data
      );

      await delay(250);

      const leadId =
        response.data?.id ?? null;

      setCreatedLeadId(
        leadId
      );

      setSubmitProgress(100);

      setSubmitStage(
        "Request submitted successfully."
      );

      setSubmitStatus({
        type: "success",
        message:
          "Your consultation request has been saved successfully.",
      });


      /* Keep the submitted form data available while
         the confirmation panel is displayed. */
      setErrors({});


    } catch (error) {

      console.error(
        "Lead submission failed:",
        error
      );

      console.error(
        "Backend error response:",
        error.response?.data
      );

      console.error(
        "HTTP status:",
        error.response?.status
      );

      console.error(
        "Requested URL:",
        LEAD_API_URL
      );

      setSubmitProgress(100);

      setSubmitStage(
        "Submission could not be completed."
      );

      let errorMessage =
        "We could not submit your project request. Please try again.";


      if (
        error.response?.status ===
        422
      ) {

        const backendMessage =
          error.response?.data
            ?.error?.message;

        errorMessage =
          backendMessage ||
          "Some information is invalid. Please review the form and try again.";
      }


      else if (
        error.response?.status >=
        500
      ) {

        errorMessage =
          "Our server encountered a temporary problem while saving your request. Please try again shortly.";
      }


      else if (
        error.code ===
        "ECONNABORTED"
      ) {

        errorMessage =
          "The request took too long to complete. Please check your connection and try again.";
      }


      else if (
        !error.response
      ) {

        errorMessage =
          "We could not connect to the consultation service. Please check your internet connection and try again.";
      }


      setSubmitStatus({
        type: "error",
        message:
          errorMessage,
      });


    } finally {

      setSubmitting(false);

    }

  };


  /* =======================================================
     DYNAMIC QUALIFICATION FIELDS
  ======================================================= */

  const renderDynamicFields = () => {

    switch (formData.projectType) {

      /* ---------------------------------------------------
         ECOMMERCE
      --------------------------------------------------- */

      case "Ecommerce Platform":

        return (
          <div className="dynamic-fields">

            <div className="dynamic-fields-header">

              <span>
                Ecommerce Requirement
              </span>

              <p>
                A few details will help us understand
                your project before we speak.
              </p>

            </div>


            <div className="form-grid">

              <div className="form-group">

                <label>
                  Business Model
                </label>

                <select
                  className="native-dark-select"
                  name="ecommerceBusinessModel"
                  value={
                    formData.ecommerceBusinessModel
                  }
                  onChange={handleChange}
                >
                  <option value="">
                    Select Business Model
                  </option>

                  <option value="B2C">
                    B2C
                  </option>

                  <option value="B2B">
                    B2B
                  </option>

                  <option value="B2B + B2C">
                    B2B + B2C
                  </option>

                  <option value="Marketplace">
                    Marketplace
                  </option>
                </select>

              </div>


              <div className="form-group">

                <label>
                  Approximate Products
                </label>

                <select
                  className="native-dark-select"
                  name="productCount"
                  value={formData.productCount}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Product Count
                  </option>

                  <option value="Below 50">
                    Below 50
                  </option>

                  <option value="50 - 500">
                    50 - 500
                  </option>

                  <option value="500 - 5000">
                    500 - 5000
                  </option>

                  <option value="5000+">
                    5000+
                  </option>
                </select>

              </div>

            </div>


            <div className="form-grid">

              <div className="form-group">

                <label>
                  Existing Website
                </label>

                <select
                  className="native-dark-select"
                  name="existingWebsite"
                  value={
                    formData.existingWebsite
                  }
                  onChange={handleChange}
                >
                  <option value="">
                    Select
                  </option>

                  <option value="Yes">
                    Yes
                  </option>

                  <option value="No">
                    No
                  </option>
                </select>

              </div>


              <div className="form-group">

                <label>
                  Payment Gateway Required?
                </label>

                <select
                  className="native-dark-select"
                  name="paymentGatewayRequired"
                  value={
                    formData.paymentGatewayRequired
                  }
                  onChange={handleChange}
                >
                  <option value="">
                    Select
                  </option>

                  <option value="Yes">
                    Yes
                  </option>

                  <option value="No">
                    No
                  </option>

                  <option value="Not Sure">
                    Not Sure
                  </option>
                </select>

              </div>

            </div>


            <div className="form-group">

              <label>
                Mobile App Required?
              </label>

              <select
                name="ecommerceMobileAppRequired"
                value={
                  formData.ecommerceMobileAppRequired
                }
                onChange={handleChange}
              >
                <option value="">
                  Select
                </option>

                <option value="Yes">
                  Yes
                </option>

                <option value="No">
                  No
                </option>

                <option value="Later">
                  Maybe Later
                </option>
              </select>

            </div>

          </div>
        );


      /* ---------------------------------------------------
         MOBILE APP
      --------------------------------------------------- */

      case "Mobile App":

        return (
          <div className="dynamic-fields">

            <div className="dynamic-fields-header">
              <span>
                Mobile App Requirement
              </span>
            </div>


            <div className="form-grid">

              <div className="form-group">

                <label>
                  Platform
                </label>

                <select
                  className="native-dark-select"
                  name="mobilePlatforms"
                  value={formData.mobilePlatforms}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Platform
                  </option>

                  <option value="Android">
                    Android
                  </option>

                  <option value="iOS">
                    iOS
                  </option>

                  <option value="Android + iOS">
                    Android + iOS
                  </option>

                  <option value="Not Sure">
                    Not Sure
                  </option>
                </select>

              </div>


              <div className="form-group">

                <label>
                  Application Type
                </label>

                <select
                  className="native-dark-select"
                  name="mobileAppType"
                  value={formData.mobileAppType}
                  onChange={handleChange}
                >
                  <option value="">
                    Select App Type
                  </option>

                  <option value="Customer App">
                    Customer App
                  </option>

                  <option value="Sales App">
                    Sales App
                  </option>

                  <option value="Service App">
                    Service App
                  </option>

                  <option value="Internal Business App">
                    Internal Business App
                  </option>

                  <option value="Other">
                    Other
                  </option>
                </select>

              </div>

            </div>


            <div className="form-group">

              <label>
                Backend / API Already Available?
              </label>

              <select
                name="backendAvailable"
                value={formData.backendAvailable}
                onChange={handleChange}
              >
                <option value="">
                  Select
                </option>

                <option value="Yes">
                  Yes
                </option>

                <option value="No">
                  No
                </option>

                <option value="Not Sure">
                  Not Sure
                </option>
              </select>

            </div>

          </div>
        );


      /* ---------------------------------------------------
         CRM
      --------------------------------------------------- */

      case "CRM / MIS Software":

        return (
          <div className="dynamic-fields">

            <div className="dynamic-fields-header">
              <span>
                CRM / MIS Requirement
              </span>
            </div>


            <div className="form-grid">

              <div className="form-group">

                <label>
                  Approximate Users
                </label>

                <select
                  className="native-dark-select"
                  name="crmUsers"
                  value={formData.crmUsers}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Users
                  </option>

                  <option value="1 - 10">
                    1 - 10
                  </option>

                  <option value="11 - 50">
                    11 - 50
                  </option>

                  <option value="51 - 200">
                    51 - 200
                  </option>

                  <option value="200+">
                    200+
                  </option>
                </select>

              </div>


              <div className="form-group">

                <label>
                  CRM Requirement
                </label>

                <select
                  className="native-dark-select"
                  name="crmType"
                  value={formData.crmType}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Requirement
                  </option>

                  <option value="Sales CRM">
                    Sales CRM
                  </option>

                  <option value="Service CRM">
                    Service CRM
                  </option>

                  <option value="Dealer CRM">
                    Dealer CRM
                  </option>

                  <option value="Customer CRM">
                    Customer CRM
                  </option>

                  <option value="MIS / Dashboard">
                    MIS / Dashboard
                  </option>

                  <option value="Integrated CRM + MIS">
                    Integrated CRM + MIS
                  </option>
                </select>

              </div>

            </div>


            <div className="form-grid">

              <div className="form-group">

                <label>
                  Current System
                </label>

                <select
                  className="native-dark-select"
                  name="currentSystem"
                  value={formData.currentSystem}
                  onChange={handleChange}
                >
                  <option value="">
                    Select
                  </option>

                  <option value="Excel">
                    Excel
                  </option>

                  <option value="Existing CRM">
                    Existing CRM
                  </option>

                  <option value="ERP">
                    ERP
                  </option>

                  <option value="Manual Process">
                    Manual Process
                  </option>

                  <option value="None">
                    None
                  </option>
                </select>

              </div>


              <div className="form-group">

                <label>
                  Mobile Sales App Required?
                </label>

                <select
                  className="native-dark-select"
                  name="crmMobileAppRequired"
                  value={
                    formData.crmMobileAppRequired
                  }
                  onChange={handleChange}
                >
                  <option value="">
                    Select
                  </option>

                  <option value="Yes">
                    Yes
                  </option>

                  <option value="No">
                    No
                  </option>

                  <option value="Not Sure">
                    Not Sure
                  </option>
                </select>

              </div>

            </div>

          </div>
        );


      /* ---------------------------------------------------
         AI
      --------------------------------------------------- */

      case "AI Solutions":

        return (
          <div className="dynamic-fields">

            <div className="dynamic-fields-header">
              <span>
                AI Requirement
              </span>
            </div>


            <div className="form-grid">

              <div className="form-group">

                <label>
                  Primary AI Use Case
                </label>

                <select
                  className="native-dark-select"
                  name="aiUseCase"
                  value={formData.aiUseCase}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Use Case
                  </option>

                  <option value="AI Assistant">
                    AI Assistant
                  </option>

                  <option value="Sales Automation">
                    Sales Automation
                  </option>

                  <option value="Customer Support">
                    Customer Support
                  </option>

                  <option value="Document Intelligence">
                    Document Intelligence
                  </option>

                  <option value="Business Analytics">
                    Business Analytics
                  </option>

                  <option value="Workflow Automation">
                    Workflow Automation
                  </option>

                  <option value="Other">
                    Other
                  </option>
                </select>

              </div>


              <div className="form-group">

                <label>
                  Existing Business Data?
                </label>

                <select
                  className="native-dark-select"
                  name="existingDataAvailable"
                  value={
                    formData.existingDataAvailable
                  }
                  onChange={handleChange}
                >
                  <option value="">
                    Select
                  </option>

                  <option value="Yes">
                    Yes
                  </option>

                  <option value="No">
                    No
                  </option>

                  <option value="Not Sure">
                    Not Sure
                  </option>
                </select>

              </div>

            </div>

          </div>
        );


      /* ---------------------------------------------------
         CUSTOM SOFTWARE
      --------------------------------------------------- */

      case "Custom Software":

        return (
          <div className="dynamic-fields">

            <div className="dynamic-fields-header">
              <span>
                Custom Software Requirement
              </span>
            </div>


            <div className="form-group">

              <label>
                What process do you want to digitise?
              </label>

              <input
                type="text"
                name="softwarePurpose"
                value={formData.softwarePurpose}
                onChange={handleChange}
                placeholder="Example: sales, service, operations, reporting..."
              />

            </div>


            <div className="form-group">

              <label>
                How is it managed today?
              </label>

              <select
                name="currentProcess"
                value={formData.currentProcess}
                onChange={handleChange}
              >
                <option value="">
                  Select
                </option>

                <option value="Excel">
                  Excel
                </option>

                <option value="Paper / Manual">
                  Paper / Manual
                </option>

                <option value="Existing Software">
                  Existing Software
                </option>

                <option value="Multiple Systems">
                  Multiple Systems
                </option>

                <option value="New Business">
                  New Business
                </option>
              </select>

            </div>

          </div>
        );


      default:

        return null;

    }

  };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      <Helmet>

        <title>
          Get Software Project Estimate | Bairagi Consultants
        </title>

        <meta
          name="description"
          content="Discuss your ecommerce, CRM, mobile app, AI or custom software project with Bairagi Consultants and request a project estimate."
        />

        <link
          rel="canonical"
          href="https://bairagi-consultants.com/contact"
        />

      </Helmet>


      <div className="contact-page">

        {/* =================================================
            PROJECT FORM
        ================================================= */}

        <section
          ref={formSectionRef}
          id="project-estimate"
          className="contact-section"
        >

          <div className="contact-form-wrapper">

            {/* LEFT */}

            <div className="contact-form-content">

              <p className="contact-section-label">
                FREE SOFTWARE CONSULTATION
              </p>


              <h1 className="contact-top-title">

                Tell Us About
                <br />
                <span>Your Project</span>

              </h1>


              <p className="contact-form-description contact-top-description">

                Share your requirement in a few minutes.
                We will review your business need, expected
                features, budget and timeline, then respond
                with the right next step.

              </p>


              <div className="contact-top-trust">

                <span>
                  <CheckCircle2 size={16} />
                  No-obligation discussion
                </span>

                <span>
                  <CheckCircle2 size={16} />
                  Response within 24 hours
                </span>

                <span>
                  <CheckCircle2 size={16} />
                  Scope before development
                </span>

              </div>


              <div className="why-list">

                {whyChooseUs.map((item) => (

                  <div
                    className="why-item"
                    key={item}
                  >

                    <CheckCircle2 size={18} />

                    <span>
                      {item}
                    </span>

                  </div>

                ))}

              </div>


              <div className="what-next">

                <span className="what-next-label">
                  SIMPLE 4-STEP PROCESS
                </span>


                <div className="what-next-step">

                  <strong>1</strong>

                  <span>
                    We review your requirement
                  </span>

                </div>


                <div className="what-next-step">

                  <strong>2</strong>

                  <span>
                    We contact you for a discovery discussion
                  </span>

                </div>


                <div className="what-next-step">

                  <strong>3</strong>

                  <span>
                    We demonstrate the recommended solution
                  </span>

                </div>


                <div className="what-next-step">

                  <strong>4</strong>

                  <span>
                    You receive scope, timeline and estimate
                  </span>

                </div>

              </div>

            </div>


            {/* RIGHT */}

            <div className="contact-form-card contact-form-card-primary">

              <div className="contact-response-badge">
                <span className="contact-response-dot" />
                Usually reviewed within 24 hours
              </div>

              {formData.projectType && (

                <div className="selected-project-banner">

                  <span>
                    Selected Project
                  </span>

                  <strong className="selected-project-value">
                    {formData.projectType}
                  </strong>

                </div>

              )}


              {submitStatus.type !== "success" && (
                <div className="contact-form-intro">
                  <h2>Start Your Project Discussion</h2>
                  <p>
                    Required fields are marked with *. It takes about 2 minutes.
                  </p>
                </div>
              )}

              {submitStatus.type === "success" ? (

                <div
                  className="consultation-confirmation"
                  role="status"
                  aria-live="polite"
                >

                  <div className="confirmation-success-icon">
                    <CheckCircle2 size={42} />
                  </div>

                  <p className="confirmation-eyebrow">
                    REQUEST RECEIVED
                  </p>

                  <h3 className="confirmation-title">
                    Your request has been saved successfully.
                  </h3>

                  <p className="confirmation-message">
                    Thank you for contacting Bairagi Consultants.
                    Our team will review your requirement and
                    respond within <strong>24 hours</strong>.
                  </p>

                  {createdLeadId && (

                    <div className="confirmation-reference">

                      <span>
                        Consultation Reference
                      </span>

                      <strong>
                        {getReferenceCode()}
                      </strong>

                    </div>

                  )}

                  <div className="confirmation-progress-card">

                    <div className="confirmation-progress-heading">

                      <span>
                        Submission completed
                      </span>

                      <strong>
                        100%
                      </strong>

                    </div>

                    <div className="confirmation-progress-track">

                      <div className="confirmation-progress-bar" />

                    </div>

                    <div className="confirmation-progress-steps">

                      <span className="done">
                        ✓ Request saved
                      </span>

                      <span className="done">
                        ✓ Reference generated
                      </span>

                      <span>
                        Response within 24 hours
                      </span>

                    </div>

                  </div>

                  <div className="confirmation-contact-title">
                    Need to continue the conversation now?
                  </div>

                  <div className="confirmation-actions">

                    <button
                      type="button"
                      className="confirmation-whatsapp-btn"
                      onClick={openWhatsApp}
                    >
                      <MessageCircle size={19} />
                      Continue on WhatsApp
                    </button>

                    <button
                      type="button"
                      className="confirmation-bot-btn"
                      onClick={() =>
                        setBotOpen(
                          (previous) => !previous
                        )
                      }
                    >
                      <Bot size={19} />
                      Chat with BOT
                    </button>

                  </div>


                  {botOpen && (

                    <div className="consultation-bot">

                      <div className="bot-header">

                        <div className="bot-heading">

                          <div className="bot-avatar">
                            <Bot size={20} />
                          </div>

                          <div>
                            <strong>
                              BC Assistant
                            </strong>

                            <span>
                              Quick project help
                            </span>
                          </div>

                        </div>

                        <button
                          type="button"
                          className="bot-close-btn"
                          aria-label="Close BOT"
                          onClick={() =>
                            setBotOpen(false)
                          }
                        >
                          <X size={18} />
                        </button>

                      </div>

                      <div className="bot-message">
                        {botMessage}
                      </div>

                      <div className="bot-quick-actions">

                        <button
                          type="button"
                          onClick={() =>
                            handleBotQuickReply(
                              "scope"
                            )
                          }
                        >
                          Project Scope
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            handleBotQuickReply(
                              "budget"
                            )
                          }
                        >
                          Budget
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            handleBotQuickReply(
                              "timeline"
                            )
                          }
                        >
                          Timeline
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            handleBotQuickReply(
                              "next"
                            )
                          }
                        >
                          What Happens Next?
                        </button>

                      </div>

                    </div>

                  )}


                  <button
                    type="button"
                    className="confirmation-new-request-btn"
                    onClick={resetConsultationForm}
                  >
                    <RotateCcw size={17} />
                    Submit Another Request
                  </button>

                </div>

              ) : (

              <form
                className="contact-form"
                onSubmit={handleSubmit}
                noValidate
              >

                {/* NAME / EMAIL */}

                <div className="form-grid">

                  <div className="form-group">

                    <label htmlFor="fullName">

                      Full Name

                      <span className="required-mark">
                        *
                      </span>

                    </label>


                    <input
                      id="fullName"
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      className={
                        errors.fullName
                          ? "input-error"
                          : ""
                      }
                    />


                    {errors.fullName && (

                      <span className="form-error">
                        {errors.fullName}
                      </span>

                    )}

                  </div>


                  <div className="form-group">

                    <label htmlFor="email">

                      Email Address

                      <span className="required-mark">
                        *
                      </span>

                    </label>


                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className={
                        errors.email
                          ? "input-error"
                          : ""
                      }
                    />


                    {errors.email && (

                      <span className="form-error">
                        {errors.email}
                      </span>

                    )}

                  </div>

                </div>


                {/* PHONE / COMPANY */}

                <div className="form-grid">

                  <div className="form-group">

                    <label htmlFor="phone">

                      Phone Number

                      <span className="required-mark">
                        *
                      </span>

                    </label>


                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      className={
                        errors.phone
                          ? "input-error"
                          : ""
                      }
                    />


                    {errors.phone && (

                      <span className="form-error">
                        {errors.phone}
                      </span>

                    )}

                  </div>


                  <div className="form-group">

                    <label htmlFor="companyName">
                      Company Name
                    </label>


                    <input
                      id="companyName"
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Enter company name"
                    />

                  </div>

                </div>


                {/* PROJECT TYPE */}

                <div className="form-group">

                  <label htmlFor="projectType">

                    Project Type

                    <span className="required-mark">
                      *
                    </span>

                  </label>


                  <CustomSelect
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    placeholder="Select Project Type"
                    options={PROJECT_TYPES.map((item) => ({
                      value: item.title,
                      label: item.title,
                    }))}
                    onChange={handleChange}
                    error={Boolean(errors.projectType)}
                  />


                  {errors.projectType && (

                    <span className="form-error">
                      {errors.projectType}
                    </span>

                  )}

                </div>


                {/* DYNAMIC FIELDS */}

                {renderDynamicFields()}


                {/* BUDGET / TIMELINE */}

                <div className="form-grid">

                  <div className="form-group">

                    <label htmlFor="budgetRange">
                      Budget Range
                    </label>


                    <CustomSelect
                      id="budgetRange"
                      name="budgetRange"
                      value={formData.budgetRange}
                      placeholder="Select Budget"
                      options={BUDGET_RANGES}
                      onChange={handleChange}
                    />

                  </div>


                  <div className="form-group">

                    <label htmlFor="timeline">
                      Expected Timeline
                    </label>


                    <CustomSelect
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      placeholder="Select Timeline"
                      options={TIMELINES}
                      onChange={handleChange}
                    />

                  </div>

                </div>


                {/* DETAILS */}

                <div className="form-group">

                  <label htmlFor="projectDetails">

                    Project Details

                    <span className="required-mark">
                      *
                    </span>

                  </label>


                  <textarea
                    id="projectDetails"
                    rows="6"
                    name="projectDetails"
                    value={formData.projectDetails}
                    onChange={handleChange}
                    placeholder="Tell us about your business problem, expected features or what you want to build..."
                    className={
                      errors.projectDetails
                        ? "input-error"
                        : ""
                    }
                  />


                  {errors.projectDetails && (

                    <span className="form-error">
                      {errors.projectDetails}
                    </span>

                  )}

                </div>


                {/* SUBMISSION PROGRESS / STATUS */}

                {(
                  submitting ||
                  submitStatus.message
                ) && (

                  <div
                    className={`submission-panel ${
                      submitStatus.type === "success"
                        ? "submission-panel-success"
                        : submitStatus.type === "error"
                        ? "submission-panel-error"
                        : "submission-panel-processing"
                    }`}
                  >

                    <div className="submission-header">

                      <div className="submission-icon">

                        {submitting ? (

                          <Loader2
                            size={22}
                            className="button-spinner"
                          />

                        ) : submitStatus.type ===
                          "success" ? (

                          <CheckCircle2
                            size={22}
                          />

                        ) : (

                          <AlertCircle
                            size={22}
                          />

                        )}

                      </div>


                      <div className="submission-content">

                        <strong>

                          {submitting
                            ? "Saving Your Consultation Request"
                            : submitStatus.type ===
                              "success"
                            ? "Request Submitted"
                            : "Submission Failed"}

                        </strong>


                        {submitStage && (

                          <span className="submission-stage">
                            {submitStage}
                          </span>

                        )}

                      </div>


                      <span className="submission-percent">
                        {submitProgress}%
                      </span>

                    </div>


                    <div
                      className="submission-progress-track"
                      role="progressbar"
                      aria-valuenow={
                        submitProgress
                      }
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >

                      <div
                        className="submission-progress-bar"
                        style={{
                          width:
                            `${submitProgress}%`,
                        }}
                      />

                    </div>


                    {submitStatus.message && (

                      <div
                        className="submission-message"
                        role={
                          submitStatus.type ===
                          "error"
                            ? "alert"
                            : "status"
                        }
                      >

                        {submitStatus.message}

                      </div>

                    )}


                    {submitStatus.type ===
                      "success" &&
                      createdLeadId && (

                        <div className="submission-reference">

                          <span>
                            Consultation Reference
                          </span>

                          <strong>
                            BC-
                            {String(
                              createdLeadId
                            ).padStart(
                              5,
                              "0"
                            )}
                          </strong>

                        </div>

                      )}

                  </div>

                )}


                {/* SUBMIT */}

                <button
                  type="submit"
                  className="contact-submit-btn"
                  disabled={submitting}
                  onClick={() => {
                    console.log(
                      "Submit button clicked"
                    );
                  }}
                >

                  {submitting ? (
                    <>
                      <Loader2
                        size={18}
                        className="button-spinner"
                      />

                      Sending Request...
                    </>
                  ) : (
                    <>
                      Submit Project Request

                      <ArrowRight size={18} />
                    </>
                  )}

                </button>


                <p className="form-helper">

                  Your information is used only to
                  review and respond to your project enquiry.

                </p>

              </form>

              )}

            </div>

          </div>

        </section>




        {/* =================================================
            PROJECT TYPES
        ================================================= */}

        <section className="contact-section">

          <div className="contact-section-header">

            <p className="contact-section-label">
              PROJECT TYPES
            </p>


            <h2 className="contact-section-title">
              What Are You Building?
            </h2>


            <p className="contact-section-subtitle">
              Select the solution closest to your requirement.
            </p>

          </div>


          <div className="project-grid">

            {PROJECT_TYPES.map((item) => (

              <button
                type="button"
                key={item.key}
                className={`project-card ${
                  formData.projectType === item.title
                    ? "project-active"
                    : ""
                }`}
                onClick={() =>
                  selectProject(item.title)
                }
              >

                <div className="project-icon">
                  {item.icon}
                </div>


                <h3>
                  {item.title}
                </h3>


                {formData.projectType ===
                  item.title && (

                  <span className="project-selected">

                    <Check size={14} />

                    Selected

                  </span>

                )}

              </button>

            ))}

          </div>

        </section>


        {/* =================================================
            CONTACT DETAILS
        ================================================= */}

        <section className="contact-section">

          <div className="contact-section-header">

            <p className="contact-section-label">
              DIRECT CONTACT
            </p>


            <h2 className="contact-section-title">
              Prefer To Speak Directly?
            </h2>

          </div>


          <div className="contact-cards-grid">

            <a
              href="mailto:bairagiconsultants@gmail.com"
              className="contact-card"
            >

              <div className="contact-card-icon">
                <Mail size={28} />
              </div>

              <h3>
                Email Us
              </h3>

              <p>
                bairagiconsultants@gmail.com
              </p>

            </a>


            <a
              href="tel:+919432169467"
              className="contact-card"
            >

              <div className="contact-card-icon">
                <Phone size={28} />
              </div>

              <h3>
                Call Us
              </h3>

              <p>
                +91 9432169467
              </p>

            </a>


            <div className="contact-card">

              <div className="contact-card-icon">
                <MapPin size={28} />
              </div>

              <h3>
                Location
              </h3>

              <p>
                Kolkata, India
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FINAL MESSAGE
        ================================================= */}

        <section className="contact-final-section">

          <div className="contact-final-card">

            <h2>
              From Requirement
              <br />
              To Working Solution
            </h2>


            <p>

              We begin by understanding the business
              problem, demonstrate the proposed solution,
              agree the scope and then move into
              implementation.

            </p>


            <button
              type="button"
              className="contact-primary-btn"
              onClick={scrollToForm}
            >

              Discuss Your Project

              <ArrowRight size={18} />

            </button>

          </div>

        </section>

      </div>
    </>
  );
}